/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Splits", description = "Understand the impact of your marketing efforts by measuring audience performance using Hightouch's Splits measurement charts.", lastUpdatedDate = new Date(1753401600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Splits",
        description: "Understand the impact of your marketing efforts by measuring audience performance using Hightouch's Splits measurement charts.",
        lastUpdatedDate: "Fri Jul 25 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "What you can measure",
          "depth": 1,
          "id": "what-you-can-measure"
        }, {
          "value": "When to use splits",
          "depth": 1,
          "id": "when-to-use-splits"
        }, {
          "value": "How audience experiments work",
          "depth": 1,
          "id": "how-audience-experiments-work"
        }, {
          "value": "Requirements",
          "depth": 0,
          "id": "requirements"
        }, {
          "value": "Setup steps",
          "depth": 0,
          "id": "setup-steps"
        }, {
          "value": "1. Split your audience",
          "depth": 1,
          "id": "1-split-your-audience"
        }, {
          "value": "2. Measure results",
          "depth": 1,
          "id": "2-measure-results"
        }, {
          "value": "Example use cases",
          "depth": 0,
          "id": "example-use-cases"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      strong: "strong",
      h3: "h3",
      ul: "ul",
      li: "li",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      ol: "ol",
      code: "code",
      img: "img",
      h4: "h4"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Understand and measure the impact of your marketing efforts across randomized groups. Splits measurement charts help you visualize the results of your experiments and evaluate the effectiveness of your campaigns."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Audience:"
        }), " Marketers and analysts who want to compare the effectiveness of different campaign strategies using real audience data."]
      }), "\n", _jsx(_components.h3, {
        id: "what-you-can-measure",
        children: "What you can measure"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Incrementality"
          }), ": Did your campaign actually cause the desired outcome?"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "A/B/n testing"
          }), ": Compare different creatives, messages, or channels."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Personalization impact"
          }), ": See if personalized experiences outperform generic ones."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Channel effectiveness"
          }), ": Identify which channels drive more downstream conversions."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "when-to-use-splits",
        children: "When to use splits"
      }), "\n", _jsx(_components.p, {
        children: "Use audience splits when you want to move beyond assumptions like:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Correlation"
          }), ": “Purchases increased after my campaign—but did the campaign cause it?”"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Attribution"
          }), ": “A user converted after seeing my email—but would they have converted anyway?”"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Running a split test helps you isolate the effect of your campaign by comparing outcomes between randomized groups. This gives you a clearer view of what’s actually driving results."
      }), "\n", _jsx(_components.h3, {
        id: "how-audience-experiments-work",
        children: "How audience experiments work"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Stage"
            }), _jsx(_components.th, {
              children: "What it means"
            }), _jsx(_components.th, {
              children: "Hightouch feature"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "1. Define your audience"
            }), _jsx(_components.td, {
              children: "Choose your target group"
            }), _jsx(_components.td, {
              children: "Build audiences"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "2. Split into groups"
            }), _jsx(_components.td, {
              children: "Randomly assign audience members"
            }), _jsx(_components.td, {
              children: "Splits"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "3. Apply a treatment"
            }), _jsx(_components.td, {
              children: "Run a campaign for one group"
            }), _jsx(_components.td, {
              children: "Syncs"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "4. Measure results"
            }), _jsx(_components.td, {
              children: "Compare outcomes between groups"
            }), _jsx(_components.td, {
              children: "Charts (Splits tab)"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "5. Interpret the impact"
            }), _jsx(_components.td, {
              children: "Evaluate statistical significance and lift"
            }), _jsx(_components.td, {
              children: "Splits Measurement"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "requirements",
        children: "Requirements"
      }), "\n", _jsx(_components.p, {
        children: "Before you can measure a split test:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://hightouch.com/docs/customer-studio/audience-snapshots",
            children: _jsx(_components.strong, {
              children: "Audience snapshots"
            })
          }), " must be enabled. Snapshots record audience membership at the time of sync."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://hightouch.com/docs/customer-studio/splits",
            children: _jsx(_components.strong, {
              children: "Split groups"
            })
          }), " must be created."]
        }), "\n", _jsxs(_components.li, {
          children: ["The audience must be ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/customer-studio/syncs",
            children: _jsx(_components.strong, {
              children: "synced"
            })
          }), " at least once after the split is created."]
        }), "\n", _jsx(_components.li, {
          children: "Each group must generate post-snapshot event data (e.g., purchases, clicks)."
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "setup-steps",
        children: "Setup steps"
      }), "\n", _jsx(_components.h3, {
        id: "1-split-your-audience",
        children: "1. Split your audience"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Open your audience in ", _jsx(_components.a, {
            href: "https://app.hightouch.com/audiences",
            children: _jsx(_components.strong, {
              children: "Customer Studio"
            })
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsx(_components.code, {
            children: "Splits"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Toggle ", _jsx(_components.strong, {
            children: "Enable split groups"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Split audience into groups"
          }), ", define your split:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "Set the number of groups (e.g., 2 for A/B tests, 3+ for A/B/n tests)."
            }), "\n", _jsx(_components.li, {
              children: "Adjust the percentage distribution between groups (e.g., 50/50)."
            }), "\n", _jsx(_components.li, {
              children: "Rename groups as needed (e.g., \"Holdout\", \"Treatment A\")."
            }), "\n"]
          }), "\n"]
        }), "\n", _jsx(_components.li, {
          children: "Optionally add more groups for A/B/n testing."
        }), "\n", _jsxs(_components.li, {
          children: ["For each group, configure syncs to destinations.", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "For example: send the treatment group to Meta Ads and the holdout group to a suppression list."
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "View Results"
          }), " to begin analysis."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/splits/ci-splits-example.png",
          alt: "Splits view"
        })
      }), "\n", _jsx(_components.h3, {
        id: "2-measure-results",
        children: "2. Measure results"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Splits"
        }), " chart helps you compare outcomes between audience groups to evaluate the impact of your campaign."]
      }), "\n", _jsx(_components.h4, {
        id: "1-configure-chart",
        children: "1. Configure chart"
      }), "\n", _jsxs(_components.p, {
        children: ["In the ", _jsx(_components.code, {
          children: "Charts > Splits"
        }), " view:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Measuring"
          }), ", select the audience you want to evaluate. A tag shows how many split groups exist."]
        }), "\n", _jsxs(_components.li, {
          children: ["Use the ", _jsx(_components.strong, {
            children: "Measurement window"
          }), " dropdown to define when Hightouch should look for events.", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "Example: “Entry → 30 days after entry”"
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose a ", _jsx(_components.strong, {
            children: "Metric"
          }), " (e.g., Conversions, Revenue). ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/campaign-intelligence/metrics",
            children: "Learn how to create a new metric"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Optional: Use the ", _jsx(_components.strong, {
            children: "Filter by"
          }), " dropdown to refine by user properties or events."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The chart will display performance over time, along with summary statistics."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/splits/splits-chart-empty.png",
          alt: "Splits chart empty state"
        })
      }), "\n", _jsx(_components.h4, {
        id: "2-interpret-results",
        children: "2. Interpret results"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Lift summary bar"
          }), " (top-right):", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Green"
              }), " = Significant positive impact"]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Red"
              }), " = Significant negative impact"]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Gray"
              }), " = Not statistically significant"]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Solid line"
          }), ": Observed average performance over time."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Shaded region"
          }), ": 95% confidence interval for each group."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If the confidence bar overlaps 0%, the result is inconclusive."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/splits/splits-chart.png",
          alt: "Splits chart expanded view"
        })
      }), "\n", _jsxs(Tip, {
        children: [" ", _jsxs(_components.p, {
          children: ["These intervals are calculated using a Bayesian method. This allows you to monitor\nresults in real time without waiting for the full experiment to complete.", " "]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "3-normalize-results",
        children: "3. Normalize results"
      }), "\n", _jsx(_components.p, {
        children: "Toggle between:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Per member (default)"
          }), ": Shows average performance per user."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "To holdout group size"
          }), ": Scales results for an even comparison."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can toggle normalization in the top-right corner. Hovering over the lift card shows raw totals."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/splits/splits-chart-normalized-per-member.png",
          alt: "Splits chart expanded view"
        })
      }), "\n", _jsx(_components.h2, {
        id: "example-use-cases",
        children: "Example use cases"
      }), "\n", _jsx(_components.h4, {
        id: "email-campaign-holdout",
        children: "Email campaign holdout"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Goal:"
        }), " Test whether sending an email increases purchases."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Split audience 50/50"
        }), "\n", _jsx(_components.li, {
          children: "Send promo to Group A, hold out Group B"
        }), "\n", _jsx(_components.li, {
          children: "Measure conversions or revenue per user"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "ad-creative-ab-test",
        children: "Ad creative A/B test"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Goal:"
        }), " See which ad performs better."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Split audience into two groups"
        }), "\n", _jsx(_components.li, {
          children: "Sync to separate ad sets in Meta or Google"
        }), "\n", _jsx(_components.li, {
          children: "Compare CTR or conversion rates"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "personalization-test",
        children: "Personalization test"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Goal:"
        }), " Measure the impact of tailored content."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Group A: Personalized product recs"
        }), "\n", _jsx(_components.li, {
          children: "Group B: Generic message"
        }), "\n", _jsx(_components.li, {
          children: "Measure engagement, clicks, or sales"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "lifecycle-messaging-experiment",
        children: "Lifecycle messaging experiment"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Goal:"
        }), " Improve onboarding outcomes."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Split new users at signup"
        }), "\n", _jsx(_components.li, {
          children: "Sync to two onboarding flows"
        }), "\n", _jsx(_components.li, {
          children: "Measure feature activation or retention after 14 days"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "channel-comparison",
        children: "Channel comparison"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Goal:"
        }), " See which marketing channel performs best."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Sync Group A to email, Group B to paid ads"
        }), "\n", _jsx(_components.li, {
          children: "Measure downstream conversion or LTV"
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
